const parseUrl=(value)=> {
  try {return new URL(value);} catch {return null;}
};

const unique=(values)=> [...new Set(values.filter(Boolean))];

const SITE_ICON_PATHS=[
  '/APP-ICON.svg',
  '/favicon.svg',
  '/favicon.png',
  '/favicon.ico',
  '/apple-touch-icon.png',
  '/apple-touch-icon-precomposed.png',
  '/android-chrome-192x192.png',
  '/android-chrome-512x512.png',
  '/icon-192x192.png',
  '/icon-512x512.png'
];

export const getSiteFaviconUrls=(url)=> {
  const parsed=parseUrl(url);
  if (!parsed || !['http:','https:'].includes(parsed.protocol)) return [];
  return SITE_ICON_PATHS.map(path=> `${parsed.origin}${path}`);
};

export const getDuckDuckGoFaviconUrl=(url)=> {
  const parsed=parseUrl(url);
  return parsed ? `https://icons.duckduckgo.com/ip3/${parsed.hostname}.ico` : null;
};

export const getGoogleFaviconUrl=(url,size=128)=> {
  const parsed=parseUrl(url);
  return parsed ? `https://www.google.com/s2/favicons?domain=${encodeURIComponent(parsed.hostname)}&sz=${size}` : null;
};

export const getGoogleOriginFaviconUrl=(url,size=128)=> {
  const parsed=parseUrl(url);
  return parsed ? `https://www.google.com/s2/favicons?sz=${size}&domain_url=${encodeURIComponent(parsed.origin)}` : null;
};

export const getFaviconUrl=(url)=> {
  const parsed=parseUrl(url);
  return parsed ? `${parsed.origin}/favicon.ico` : null;
};

export const isFaviconServiceUrl=(value)=> {
  const parsed=parseUrl(value);
  if (!parsed) return false;
  const host=parsed.hostname.toLowerCase();
  return host==='www.google.com' && parsed.pathname.startsWith('/s2/favicons') || host==='icons.duckduckgo.com';
};

export const getAutomaticFaviconCandidates=(url)=> unique([
  ...getSiteFaviconUrls(url),
  getGoogleFaviconUrl(url),
  getGoogleOriginFaviconUrl(url),
  getDuckDuckGoFaviconUrl(url)
]);

export const getNeutralFaviconDataUrl=(url)=> {
  const parsed=parseUrl(url);
  const label=(parsed?.hostname || '?').replace(/^www\./,'').charAt(0).toUpperCase() || '?';
  const svg=`<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64"><rect width="64" height="64" rx="14" fill="#eef2ff"/><circle cx="32" cy="32" r="18" fill="#4f46e5" opacity=".12"/><text x="32" y="39" text-anchor="middle" font-family="-apple-system,BlinkMacSystemFont,Segoe UI,Arial,sans-serif" font-size="22" font-weight="700" fill="#4338ca">${label.replace(/[<>&"']/g,'')}</text></svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

export const getDefaultFaviconUrl=(url)=> getGoogleFaviconUrl(url) || getGoogleOriginFaviconUrl(url) || getDuckDuckGoFaviconUrl(url) || getFaviconUrl(url);

export const getFaviconCandidates=(bookmark,pb)=> {
  const candidates=[];
  const push=(value)=> {if (value && !candidates.includes(value)) candidates.push(value);};
  if (bookmark?.faviconfile && pb) push(pb.files.getUrl(bookmark,bookmark.faviconfile));

  const stored=bookmark?.faviconurl;
  if (stored && !isFaviconServiceUrl(stored)) push(stored);
  getSiteFaviconUrls(bookmark?.url).forEach(push);
  push(stored);
  push(getGoogleFaviconUrl(bookmark?.url));
  push(getGoogleOriginFaviconUrl(bookmark?.url));
  push(getDuckDuckGoFaviconUrl(bookmark?.url));
  push(getNeutralFaviconDataUrl(bookmark?.url));
  return candidates;
};

export const validateImageUrl=async (url,timeoutMs=3500)=> new Promise(resolve=> {
  if (!url) {resolve(false);return;}
  const img=new Image();
  let settled=false;
  const finish=(value)=> {
    if (settled) return;
    settled=true;
    img.onload=null;
    img.onerror=null;
    resolve(value);
  };
  img.onload=()=> finish(img.naturalWidth>0 && img.naturalHeight>0);
  img.onerror=()=> finish(false);
  img.referrerPolicy='no-referrer';
  img.src=url;
  window.setTimeout(()=> finish(false),timeoutMs);
});

const firstValidImageUrl=async (candidates,timeoutMs)=> {
  const uniqueCandidates=unique(candidates);
  if (!uniqueCandidates.length) return null;
  const results=await Promise.all(uniqueCandidates.map(async candidate=> ({candidate,valid:await validateImageUrl(candidate,timeoutMs)})));
  return results.find(result=> result.valid)?.candidate || null;
};

export const resolveBestFaviconUrl=async (url,{currentUrl='',timeoutMs=3000}={})=> {
  const parsed=parseUrl(url);
  if (!parsed || !['http:','https:'].includes(parsed.protocol)) return null;

  if (currentUrl && !isFaviconServiceUrl(currentUrl) && await validateImageUrl(currentUrl,timeoutMs)) return currentUrl;

  const siteOwned=await firstValidImageUrl(getSiteFaviconUrls(url),timeoutMs);
  if (siteOwned) return siteOwned;

  return firstValidImageUrl([
    currentUrl,
    getGoogleFaviconUrl(url),
    getGoogleOriginFaviconUrl(url),
    getDuckDuckGoFaviconUrl(url)
  ],timeoutMs);
};

export const resizeImage=(file,maxWidth=48,maxHeight=48)=> new Promise(resolve=> {
  const canvas=document.createElement('canvas');
  const ctx=canvas.getContext('2d');
  const img=new Image();
  img.onload=()=> {
    const scale=Math.min(maxWidth/img.width,maxHeight/img.height,1);
    canvas.width=Math.max(1,Math.round(img.width*scale));
    canvas.height=Math.max(1,Math.round(img.height*scale));
    ctx.drawImage(img,0,0,canvas.width,canvas.height);
    canvas.toBlob(resolve,'image/png');
    URL.revokeObjectURL(img.src);
  };
  img.src=URL.createObjectURL(file);
});

export const fileToDataUrl=(file)=> new Promise((resolve,reject)=> {
  const reader=new FileReader();
  reader.onload=(event)=> resolve(event.target.result);
  reader.onerror=reject;
  reader.readAsDataURL(file);
});

export const getFaviconDisplayUrl=(bookmark,pb)=> getFaviconCandidates(bookmark,pb)[0] || getNeutralFaviconDataUrl(bookmark?.url);

export const shouldUpdateFaviconUrl=(oldUrl,newUrl,hasFaviconFile)=> {
  if (hasFaviconFile) return false;
  const oldParsed=parseUrl(oldUrl);
  const newParsed=parseUrl(newUrl);
  if (!oldParsed || !newParsed) return true;
  return oldParsed.hostname!==newParsed.hostname;
};